import { requestApi } from "../api/client.js";

export type TaskStatus = "active" | "archived" | "paused";
export type TaskKind =
  | "check"
  | "cleaning"
  | "log"
  | "maintenance"
  | "reminder_follow_up"
  | "training";

export interface TaskSchedule {
  active_from: string | null;
  active_to: string | null;
  due_window_end: string | null;
  due_window_start: string | null;
  one_off_due_at: string | null;
  reminder_offsets_min: number[];
  rrule: string | null;
}

export interface TaskDefinition {
  asset_id: string | null;
  assignee_staff_profile_id: string | null;
  cleaning_plan_item_id: string | null;
  current_version: number;
  id: string;
  instructions: string | null;
  location_id: string;
  origin: "manager" | "plan_generated";
  photo_policy: "optional" | "requested" | "required_by_business";
  process_method_id: string | null;
  record_type_code: string | null;
  schedule: TaskSchedule | null;
  status: TaskStatus;
  supplier_id: string | null;
  task_kind: TaskKind;
  title: string;
}

export interface TaskOccurrence {
  asset_id: string | null;
  assignee_staff_profile_id: string | null;
  completed_record_id: string | null;
  definition_version: number;
  due_at: string;
  id: string;
  local_date: string;
  location_id: string;
  record_type_code: string | null;
  status: "cancelled" | "completed" | "due" | "missed" | "skipped" | "upcoming";
  task_definition_id: string;
  task_kind: TaskKind;
  title: string;
  window_start_at: string | null;
}

interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

export function listTaskDefinitions(
  locationId: string,
  cursor?: string,
): Promise<Page<TaskDefinition>> {
  const query = new URLSearchParams({ location_id: locationId, limit: "200" });
  if (cursor) query.set("cursor", cursor);
  return requestApi(`/task-definitions?${query.toString()}`);
}

export function saveTaskDefinition(
  locationId: string,
  input: Record<string, unknown>,
  current?: TaskDefinition,
): Promise<TaskDefinition> {
  return requestApi<TaskDefinition>(
    current ? `/task-definitions/${current.id}` : "/task-definitions",
    {
      body: JSON.stringify(
        current
          ? input
          : {
              ...input,
              current_version: 1,
              id: crypto.randomUUID(),
              location_id: locationId,
              status: "active",
            },
      ),
      ...(current
        ? {}
        : { headers: { "Idempotency-Key": crypto.randomUUID() } }),
      method: current ? "PATCH" : "POST",
    },
  );
}

export function archiveTaskDefinition(id: string): Promise<void> {
  return requestApi(`/task-definitions/${id}`, { method: "DELETE" });
}

export function listTaskOccurrences(
  locationId: string,
  input: {
    assignee: "any" | "me";
    from?: string;
    status?: TaskOccurrence["status"];
    to?: string;
  },
  cursor?: string,
): Promise<Page<TaskOccurrence>> {
  const query = new URLSearchParams({
    assignee: input.assignee,
    limit: "200",
    location_id: locationId,
  });
  if (input.from) query.set("from", input.from);
  if (input.to) query.set("to", input.to);
  if (input.status) query.set("status", input.status);
  if (cursor) query.set("cursor", cursor);
  return requestApi(`/task-occurrences?${query.toString()}`);
}

export function skipOccurrence(
  id: string,
  note?: string,
): Promise<TaskOccurrence> {
  return requestApi<TaskOccurrence>(`/task-occurrences/${id}/skip`, {
    body: JSON.stringify(note ? { note } : {}),
    method: "POST",
  });
}

export function cancelOccurrence(id: string): Promise<TaskOccurrence> {
  return requestApi<TaskOccurrence>(`/task-occurrences/${id}/cancel`, {
    method: "POST",
  });
}

export type FriendlyRecurrence =
  | { frequency: "daily"; interval: number }
  | { frequency: "monthly"; interval: number; monthDay: number }
  | { frequency: "weekly"; interval: number; weekdays: string[] };

export function recurrenceToRRule(recurrence: FriendlyRecurrence): string {
  const interval =
    recurrence.interval === 1 ? "" : `;INTERVAL=${recurrence.interval}`;
  if (recurrence.frequency === "daily") return `FREQ=DAILY${interval}`;
  if (recurrence.frequency === "monthly")
    return `FREQ=MONTHLY${interval};BYMONTHDAY=${recurrence.monthDay}`;
  const ordered = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"].filter((day) =>
    recurrence.weekdays.includes(day),
  );
  return `FREQ=WEEKLY${interval};BYDAY=${ordered.join(",")}`;
}

export function rruleToFriendly(
  rrule: string | null | undefined,
): FriendlyRecurrence {
  const interval = Number(/INTERVAL=(\d+)/.exec(rrule ?? "")?.[1] ?? 1);
  if (rrule?.includes("FREQ=MONTHLY")) {
    return {
      frequency: "monthly",
      interval,
      monthDay: Number(/BYMONTHDAY=(\d+)/.exec(rrule)?.[1] ?? 1),
    };
  }
  if (rrule?.includes("FREQ=WEEKLY")) {
    return {
      frequency: "weekly",
      interval,
      weekdays: /BYDAY=([^;]+)/.exec(rrule)?.[1]?.split(",") ?? ["MO"],
    };
  }
  return { frequency: "daily", interval };
}

export function taskStatusTone(
  status: TaskOccurrence["status"],
): "attention" | "danger" | "neutral" | "positive" {
  return status === "completed"
    ? "positive"
    : status === "missed"
      ? "danger"
      : status === "due"
        ? "attention"
        : "neutral";
}
